// utils/GatePassUtils.ts - Utility functions for Gate Pass functionality

import { FormData, UserInfo, Gate, Personnel } from '../types/GatePassTypes';
import { Gate as GateManagementGate } from '../types/GatePassTypes';

// Helper function to create default form data
export const createDefaultFormData = (userInfo?: UserInfo): FormData => ({
  name: userInfo?.name || '',
  email: userInfo?.email || '',
  companyName: userInfo?.companyName || '',
  idDetails: '',
  consignmentDetails: '',
  detailedDescription: '',
  regionId: null,
  requestType: 'new',
  passType: 'single_visit',
  contractNumber: '',
  startDate: '',
  endDate: '',
  passIdentificationName: '',
  approverEmail: '',
  approverUserId: '',
  reviewerEmail: '',
  reviewerUserId: '',
  selectedFunctionId: undefined,
  selectedCompanyId: undefined
});

// Helper function to merge form data safely
export const mergeFormData = (defaultData: FormData, loadedData: any): FormData => {
  if (!loadedData || typeof loadedData !== 'object') {
    console.warn('Invalid form data received from draft, using defaults');
    return defaultData;
  }

  return {
    name: loadedData.name || defaultData.name,
    email: loadedData.email || defaultData.email,
    companyName: loadedData.companyName || defaultData.companyName,
    idDetails: loadedData.idDetails || defaultData.idDetails,
    consignmentDetails: loadedData.consignmentDetails || defaultData.consignmentDetails,
    detailedDescription: loadedData.detailedDescription || defaultData.detailedDescription,
    regionId: loadedData.regionId ?? defaultData.regionId,
    requestType: loadedData.requestType || defaultData.requestType,
    passType: loadedData.passType || defaultData.passType,
    contractNumber: loadedData.contractNumber || defaultData.contractNumber,
    startDate: loadedData.startDate || defaultData.startDate,
    endDate: loadedData.endDate || defaultData.endDate,
    passIdentificationName: loadedData.passIdentificationName || defaultData.passIdentificationName,
    approverEmail: loadedData.approverEmail || defaultData.approverEmail,
    approverUserId: loadedData.approverUserId || defaultData.approverUserId,
    reviewerEmail: loadedData.reviewerEmail || defaultData.reviewerEmail,
    reviewerUserId: loadedData.reviewerUserId || defaultData.reviewerUserId,
    selectedFunctionId: loadedData.selectedFunctionId ?? defaultData.selectedFunctionId,
    selectedCompanyId: loadedData.selectedCompanyId ?? defaultData.selectedCompanyId
  };
};

// Type adapter function
export const adaptGateForForm = (gate: GateManagementGate): Gate => ({
  ...gate,
  locationId: gate.regionId,
  regionId: gate.regionId
});

// Date validation
export const validateDateRange = (startDate: string, endDate: string): boolean => {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return end >= start;
  }
  return true;
};

// Check if visa is expired
export const isVisaExpired = (expiryDate: string): boolean => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

// Format date for display
export const formatDate = (dateString: string): string => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get region name from regions array
export const getRegionName = (regionId: number | null, regions: any[]): string => {
  if (!regionId) return 'Not specified';
  const region = regions.find(r => r.regionId === regionId);
  return region ? `${region.regionName} (${region.regionCode})` : `Region ID: ${regionId}`;
};

// Count expired visas in personnel list
export const getExpiredVisaCount = (personnel: Personnel[]): number => {
  return personnel.filter(p => isVisaExpired(p.visaExpiry)).length;
};

// Get expired personnel
export const getExpiredPersonnel = (personnel: Personnel[]): Personnel[] => {
  return personnel.filter(p => isVisaExpired(p.visaExpiry));
};

// Format last saved time
export const formatLastSavedTime = (lastSavedAt: Date | null): string => {
  if (!lastSavedAt) return '';
  return `Last saved: ${lastSavedAt.toLocaleTimeString()}`;
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate file type and size
export const validateFile = (file: File, maxSizeMB: number, acceptedTypes: string[]): { isValid: boolean; error?: string } => {
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${maxSizeMB}MB`
    };
  }

  // Check file type
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!acceptedTypes.includes(fileExtension)) {
    return {
      isValid: false,
      error: `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`
    };
  }

  return { isValid: true };
};

// Create user-friendly error message for API errors
export const createErrorMessage = (error: unknown): string => {
  const isError = error instanceof Error;
  let userFriendlyMessage = 'An unexpected error occurred. Please try again.';
  
  if (isError) {
    if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
      userFriendlyMessage = 'Unable to connect to server. Please check your internet connection and try again.';
    } else if (error.message.includes('Authentication failed')) {
      userFriendlyMessage = 'Authentication expired. Please sign out and sign in again.';
    } else {
      userFriendlyMessage = error.message;
    }
  }
  
  return userFriendlyMessage;
};

// Generate submission date string
export const generateSubmissionDateString = (): string => {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Debounce function for auto-save
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Check if value is empty or null
export const isEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

// Safe string conversion
export const safeString = (value: any): string => {
  return value?.toString() || '';
};

// Safe number conversion
export const safeNumber = (value: any): number | null => {
  const num = Number(value);
  return isNaN(num) ? null : num;
};